// The sample policy assistant: scripted answers from a bundled sample
// behaviour policy, capped at three questions per tab.
//
// Two places use it, and it has to be the same in both:
//
//   demo  The whole demo answers from this, because the demo school has no
//         uploaded documents and a box that answered anything would both
//         misrepresent the product and invite people to test it as a general
//         chatbot.
//   tour  The cover tour's sample lesson answers from this too, in the demo AND
//         in a real school. The tour used to render the panel disabled
//         ("Available once you are covering a real lesson"), which spotlit a
//         feature and then refused to show it. Calling the real assistant during
//         a tour would be wrong for different reasons: the sample lesson's rules
//         card is not the school's real policy, a school two days in may have
//         uploaded nothing yet, and it spends real credits on a walkthrough.
//
// Each scope keeps its own count, so the three demo questions and the three
// tour questions do not eat into each other. Per tab, because each demo tab is
// a different person's session.

import type { PolicyChatResponse } from '@/lib/api';

export const SAMPLE_POLICY_CHAT_LIMIT = 3;
export const SAMPLE_POLICY_DOC = 'Sample Behaviour Policy';

export type SamplePolicyScope = 'demo' | 'tour';

const COUNT_KEYS: Record<SamplePolicyScope, string> = {
  demo: 'cmc_demo_policy_chat_count',
  tour: 'cmc_tour_policy_chat_count',
};

export function readSamplePolicyCount(scope: SamplePolicyScope): number {
  try {
    return Number(sessionStorage.getItem(COUNT_KEYS[scope]) ?? '0') || 0;
  } catch {
    return 0;
  }
}

function writeSamplePolicyCount(scope: SamplePolicyScope, n: number) {
  try {
    sessionStorage.setItem(COUNT_KEYS[scope], String(n));
  } catch {
    // Private browsing or a full quota: the cap simply does not persist.
  }
}

/** Questions left in this tab for the scope. What the panel shows before the
 *  first question is asked, so nobody spends three unaware there were three. */
export function samplePolicyRemaining(scope: SamplePolicyScope): number {
  return Math.max(0, SAMPLE_POLICY_CHAT_LIMIT - readSamplePolicyCount(scope));
}

/**
 * Answer one question from the sample policy, spending one of the scope's
 * three. Mirrors the production contract exactly: grounded answers cite a
 * document and page; anything the sample does not cover comes back
 * grounded:false so the panel shows the honest contacts redirect; past the cap
 * it returns limitReached with no answer.
 *
 * The three grounded topics match the rules on the sample school's Key School
 * Rules card, so a person who reads the card and then asks about it gets an
 * answer that agrees with what they just read.
 */
export function answerFromSamplePolicy(rawQuestion: string, scope: SamplePolicyScope): PolicyChatResponse {
  const question = rawQuestion.toLowerCase();
  const asked = readSamplePolicyCount(scope);
  if (asked >= SAMPLE_POLICY_CHAT_LIMIT) {
    return { grounded: false, limitReached: true, answer: '', citations: [], docsAvailable: 1, docTitles: [SAMPLE_POLICY_DOC], remaining: 0 };
  }
  writeSamplePolicyCount(scope, asked + 1);
  const remaining = SAMPLE_POLICY_CHAT_LIMIT - (asked + 1);

  const grounded =
    /toilet|bathroom|loo\b|leave the (class|room)|corridor pass|out of the room/.test(question) ? {
      answer: 'Yes. One pupil may leave at a time with a corridor pass. The behaviour policy says pupils should not be refused reasonable toilet requests, but only one pupil may be out of the room at any time and they must sign out on the board.',
      citations: [{ title: SAMPLE_POLICY_DOC, pageStart: 4, pageEnd: 4, quote: 'Only one pupil may be out of the classroom at any time, carrying a corridor pass.' }],
    } :
    /phone|mobile/.test(question) ? {
      answer: 'Mobile phones must be switched off and in bags during lessons. If a pupil uses one, ask them once to put it away; if it continues, confiscate it to the front desk and log the incident for the class teacher.',
      citations: [{ title: SAMPLE_POLICY_DOC, pageStart: 6, pageEnd: 6, quote: 'Phones seen or heard during lessons are confiscated to the front desk until the end of the school day.' }],
    } :
    /warning|behaviour|behavior|misbehav|disrupt|shout|detention|sanction/.test(question) ? {
      answer: 'Use the three-step warning system: a verbal warning, then a formal warning recorded on the board, then removal to the partner classroom. Cover teachers do not issue detentions; log the incident for the class teacher, who decides on any sanction.',
      citations: [{ title: SAMPLE_POLICY_DOC, pageStart: 2, pageEnd: 3, quote: 'Staff follow the three-step warning system consistently in every classroom.' }],
    } : null;

  if (grounded) {
    return { grounded: true, ...grounded, docsAvailable: 1, docTitles: [SAMPLE_POLICY_DOC], remaining };
  }
  return { grounded: false, citations: [], docsAvailable: 1, docTitles: [SAMPLE_POLICY_DOC], remaining };
}
